<script setup>
import { computed, ref } from 'vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useI18n } from 'vue-i18n';

import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';
import { MESSAGE_VARIANTS } from '../constants';

const { content, contentAttributes, variant } = useMessageContext();
const { t } = useI18n();

const interactive = computed(() => contentAttributes.value?.interactive ?? {});

const pixCode = computed(
  () => interactive.value.pixCode || interactive.value.pix_code || ''
);
const merchantName = computed(
  () => interactive.value.merchantName || interactive.value.merchant_name || ''
);
const pixUrl = computed(() => {
  const value = interactive.value.pixUrl || interactive.value.pix_url;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password
      ? value
      : '';
  } catch {
    return '';
  }
});
const pixButtonClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.BOT
    ? 'bg-[#30343b] text-[#f1f5f9] hover:bg-[#3c424c]'
    : 'bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-2'
);
const copyStatus = ref('');
const copyPix = async (link = false) => {
  const value = link ? pixUrl.value : pixCode.value;
  if (!value) return;
  try {
    await copyTextToClipboard(value);
    copyStatus.value = t(
      link
        ? 'CONVERSATION.INTERACTIVE.PIX.LINK_COPIED'
        : 'CONVERSATION.INTERACTIVE.PIX.COPIED'
    );
  } catch {
    copyStatus.value = t('CONVERSATION.INTERACTIVE.PIX.COPY_ERROR');
  }
};

const headerType = computed(() => interactive.value.headerType || '');
const headerText = computed(() => interactive.value.headerText || '');
const headerMediaUrl = computed(() => interactive.value.headerMediaUrl || '');
const buttons = computed(() => interactive.value.buttons || []);

const controlClass = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.BOT) {
    return 'border-[#cbd5e1] bg-white/85 text-[#334155] shadow-sm';
  }

  if (variant.value === MESSAGE_VARIANTS.USER) {
    return 'border-n-strong bg-n-solid-1 text-n-slate-12 shadow-sm dark:bg-n-solid-2';
  }

  if (variant.value === MESSAGE_VARIANTS.PRIVATE) {
    return 'border-n-amber-8 bg-n-solid-1/70 text-n-amber-12 shadow-sm';
  }

  return 'border-white/30 bg-white/15 text-white shadow-sm';
});

const controlIconClass = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.BOT) {
    return 'bg-[#eef2ff] text-n-brand';
  }

  if (variant.value === MESSAGE_VARIANTS.USER) {
    return 'bg-n-brand/10 text-n-brand';
  }

  if (variant.value === MESSAGE_VARIANTS.PRIVATE) {
    return 'bg-n-amber-4 text-n-amber-11';
  }

  return 'bg-white/15 text-current';
});

const action = computed(() => {
  const type = interactive.value.type;
  if (type === 'order_details')
    return {
      icon: 'i-hugeicons-money-receive-01',
      label: t('CONVERSATION.INTERACTIVE.PIX.COPY'),
    };
  if (type === 'cta_url') {
    return {
      icon: 'i-lucide-external-link',
      label: interactive.value.ctaDisplayText,
      url: interactive.value.ctaUrl,
    };
  }
  if (type === 'location_request_message') {
    return {
      icon: 'i-lucide-map-pin',
      label: t('CONVERSATION.INTERACTIVE.PREVIEW.SEND_LOCATION'),
    };
  }
  if (type === 'flow') {
    return {
      icon: 'i-lucide-list-checks',
      label: interactive.value.flowCta,
    };
  }
  if (type === 'catalog_message') {
    return {
      icon: 'i-lucide-shopping-bag',
      label: t('CONVERSATION.INTERACTIVE.PREVIEW.VIEW_CATALOG'),
    };
  }
  return null;
});
</script>

<template>
  <BaseBubble
    data-bubble-name="whatsapp-interactive"
    meta-padding-class="px-4 pb-3"
  >
    <div class="interactive-content">
      <div v-if="headerType === 'text'" class="px-4 pt-3 text-sm font-semibold">
        {{ headerText }}
      </div>
      <img
        v-else-if="headerType === 'image' && headerMediaUrl"
        :src="headerMediaUrl"
        :alt="t('CONVERSATION.INTERACTIVE.HEADER_TYPES.IMAGE')"
        class="max-h-64 w-full object-cover"
      />
      <video
        v-else-if="headerType === 'video' && headerMediaUrl"
        :src="headerMediaUrl"
        class="max-h-64 w-full bg-black object-contain"
        controls
        preload="metadata"
      />
      <a
        v-else-if="headerType === 'document' && headerMediaUrl"
        :href="headerMediaUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="mx-3 mt-3 flex items-center gap-2 rounded-lg border border-current/20 bg-white/10 px-3 py-2 text-current"
      >
        <span class="i-lucide-file-text size-4 shrink-0" />
        <span class="min-w-0 truncate text-sm font-medium">
          {{
            interactive.headerFilename ||
            t('CONVERSATION.INTERACTIVE.HEADER_TYPES.DOCUMENT')
          }}
        </span>
      </a>

      <div class="px-4 py-2.5">
        <div
          v-dompurify-html="content"
          class="prose prose-bubble whitespace-pre-line text-sm"
        />
        <p v-if="interactive.footerText" class="mb-0 mt-2 text-xs opacity-70">
          {{ interactive.footerText }}
        </p>
      </div>

      <div
        v-if="interactive.type === 'order_details'"
        class="pix-payment-info mx-4 mb-3 rounded-lg p-3"
      >
        <p class="mb-2 flex items-center gap-2 text-sm font-semibold">
          <span class="i-hugeicons-money-receive-01 size-4" />
          {{ t('CONVERSATION.INTERACTIVE.PIX.TITLE') }}
        </p>
        <p class="mb-1 text-xs">{{ interactive.description }}</p>
        <strong class="text-lg">{{
          new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(Number(interactive.amount) || 0)
        }}</strong>
        <p class="mb-0 mt-1 text-xs">{{ merchantName }}</p>
      </div>
      <div
        v-if="buttons.length"
        class="interactive-actions relative flex flex-col gap-2 px-3 pb-2.5 pt-2.5"
      >
        <div
          v-for="button in buttons"
          :key="button.id"
          class="flex min-h-10 cursor-default items-center gap-2.5 rounded-xl border px-2.5 py-1.5 text-sm font-semibold"
          :class="controlClass"
        >
          <span
            class="grid size-7 shrink-0 place-content-center rounded-lg"
            :class="controlIconClass"
          >
            <span class="i-lucide-mouse-pointer-click size-3.5" />
          </span>
          <span class="min-w-0 flex-1 text-left leading-5">
            {{ button.title }}
          </span>
          <span class="i-lucide-chevron-right size-4 shrink-0 opacity-55" />
        </div>
      </div>

      <div v-else-if="interactive.type === 'order_details'" class="px-3 pb-3">
        <button
          type="button"
          class="pix-copy-button flex min-h-10 w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium"
          :class="pixButtonClass"
          :disabled="!pixCode"
          @click.stop="copyPix(false)"
        >
          <span class="i-lucide-copy size-4" />
          {{ t('CONVERSATION.INTERACTIVE.PIX.COPY') }}
        </button>
        <button
          v-if="pixUrl"
          type="button"
          class="pix-copy-button mt-2 flex min-h-10 w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium"
          :class="pixButtonClass"
          @click.stop="copyPix(true)"
        >
          <span class="i-lucide-link size-4" />
          {{ t('CONVERSATION.INTERACTIVE.PIX.COPY_LINK') }}
        </button>
        <p v-if="copyStatus" role="status" class="mb-0 mt-2 text-xs">
          {{ copyStatus }}
        </p>
      </div>
      <component
        :is="action?.url ? 'a' : 'div'"
        v-else-if="action"
        :href="action.url || undefined"
        :target="action.url ? '_blank' : undefined"
        :rel="action.url ? 'noopener noreferrer' : undefined"
        class="mx-3 mb-2.5 mt-2.5 flex min-h-10 items-center gap-2.5 rounded-xl border px-2.5 py-1.5 text-sm font-semibold"
        :class="controlClass"
      >
        <span
          class="grid size-7 shrink-0 place-content-center rounded-lg"
          :class="controlIconClass"
        >
          <span :class="action.icon" class="size-3.5" />
        </span>
        <span class="min-w-0 flex-1 text-left leading-5">
          {{ action.label }}
        </span>
        <span class="i-lucide-chevron-right size-4 shrink-0 opacity-55" />
      </component>
    </div>
  </BaseBubble>
</template>

<style scoped>
.pix-payment-info {
  border: 1px solid rgb(148 163 184 / 22%);
}
.pix-copy-button {
  border: 1px solid rgb(148 163 184 / 28%);
  transition: background-color 150ms;
}
.pix-copy-button:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}
.pix-copy-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.interactive-content {
  overflow: hidden;
  border-radius: inherit;
}

.interactive-actions::before {
  position: absolute;
  top: 0;
  right: 1rem;
  left: 1rem;
  height: 1px;
  content: '';
  background: linear-gradient(
    90deg,
    transparent,
    currentcolor 20%,
    currentcolor 80%,
    transparent
  );
  opacity: 0.2;
}
</style>
